import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { BarChart3, Search, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { TicketCard } from "@/components/ticket/TicketCard";
import { useApi } from "@/hooks/use-api";
import { toDate } from "@/lib/dates";
const OPEN_STATUSES = [
    "new",
    "investigating",
    "awaiting_reporter",
    "issue_created",
    "fixing",
    "fix_failed",
    "staging_ready",
    "testing",
    "approved",
];
const RESOLVED_STATUSES = ["merged", "closed", "wont_fix"];
const FILTER_OPTIONS = [
    { value: "all", label: "All Tickets" },
    { value: "open", label: "Open" },
    { value: "resolved", label: "Resolved" },
    { value: "new", label: "New" },
    { value: "investigating", label: "Investigating" },
    { value: "awaiting_reporter", label: "Awaiting Response" },
    { value: "issue_created", label: "Issue Created" },
    { value: "fixing", label: "Fix In Progress" },
    { value: "fix_failed", label: "Fix Failed" },
    { value: "staging_ready", label: "Ready for Testing" },
    { value: "approved", label: "Approved" },
    { value: "merged", label: "Merged" },
    { value: "closed", label: "Closed" },
    { value: "wont_fix", label: "Won't Fix" },
];
export function AdminDashboard() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const { callApi } = useApi();
    const fetchTickets = useCallback(async () => {
        try {
            setLoading(true);
            const result = await callApi("admin.listAllTickets", {});
            setTickets(result.tickets.map((t) => ({
                ...t,
                createdAt: toDate(t.createdAt),
                updatedAt: toDate(t.updatedAt),
            })));
        }
        catch (err) {
            console.error("Failed to fetch tickets:", err);
        }
        finally {
            setLoading(false);
        }
    }, [callApi]);
    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);
    const stats = {
        total: tickets.length,
        open: tickets.filter((t) => OPEN_STATUSES.includes(t.status)).length,
        investigating: tickets.filter((t) => t.status === "investigating").length,
        resolved: tickets.filter((t) => RESOLVED_STATUSES.includes(t.status)).length,
    };
    const filteredTickets = tickets.filter((ticket) => {
        if (filter === "all")
            return true;
        if (filter === "open")
            return OPEN_STATUSES.includes(ticket.status);
        if (filter === "resolved")
            return RESOLVED_STATUSES.includes(ticket.status);
        return ticket.status === filter;
    });
    const statCards = [
        {
            title: "Total Tickets",
            value: stats.total,
            icon: BarChart3,
            color: "text-blue-400",
            bg: "bg-blue-500/15",
        },
        {
            title: "Open",
            value: stats.open,
            icon: Clock,
            color: "text-amber-400",
            bg: "bg-amber-500/15",
        },
        {
            title: "Investigating",
            value: stats.investigating,
            icon: Search,
            color: "text-violet-400",
            bg: "bg-violet-500/15",
        },
        {
            title: "Resolved",
            value: stats.resolved,
            icon: CheckCircle2,
            color: "text-green-400",
            bg: "bg-green-500/15",
        },
    ];
    return (_jsxs("div", { className: "p-6 lg:p-8", children: [_jsxs("div", { className: "mb-6", children: [_jsx("h1", { className: "text-2xl font-bold tracking-tight", children: "Admin Dashboard" }), _jsx("p", { className: "text-sm text-muted-foreground", children: "Overview of all tickets and system status" })] }), loading ? (_jsx("div", { className: "mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4", children: Array.from({ length: 4 }).map((_, i) => (_jsx(Skeleton, { className: "h-28 rounded-xl" }, i))) })) : (_jsx("div", { className: "mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4", children: statCards.map((stat) => (_jsxs(Card, { children: [_jsxs(CardHeader, { className: "flex flex-row items-center justify-between pb-2", children: [_jsx(CardTitle, { className: "text-sm font-medium text-muted-foreground", children: stat.title }), _jsx("div", { className: `flex h-8 w-8 items-center justify-center rounded-lg ${stat.bg}`, children: _jsx(stat.icon, { className: `h-4 w-4 ${stat.color}` }) })] }), _jsx(CardContent, { children: _jsx("p", { className: "text-3xl font-bold", children: stat.value }) })] }, stat.title))) })), _jsxs("div", { className: "mb-4 flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-semibold", children: "All Tickets" }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "w-56", children: _jsxs(Select, { value: filter, onValueChange: (v) => setFilter(v), children: [_jsx(SelectTrigger, { children: _jsx(SelectValue, { placeholder: "Filter tickets" }) }), _jsx(SelectContent, { children: FILTER_OPTIONS.map((opt) => (_jsx(SelectItem, { value: opt.value, children: opt.label }, opt.value))) })] }) }), _jsxs("span", { className: "text-sm text-muted-foreground", children: [filteredTickets.length, " ticket", filteredTickets.length !== 1 ? "s" : ""] })] })] }), loading ? (_jsx("div", { className: "space-y-3", children: Array.from({ length: 5 }).map((_, i) => (_jsx(Skeleton, { className: "h-28 w-full rounded-xl" }, i))) })) : filteredTickets.length === 0 ? (_jsxs("div", { className: "flex flex-col items-center justify-center rounded-xl border border-dashed py-12", children: [_jsx(AlertTriangle, { className: "mb-3 h-10 w-10 text-muted-foreground/40" }), _jsx("p", { className: "text-sm text-muted-foreground", children: "No tickets match the selected filter" })] })) : (_jsx("div", { className: "space-y-3", children: filteredTickets.map((ticket) => (_jsx(TicketCard, { ticket: ticket, showReporter: true }, ticket.id))) }))] }));
}
